import { Component, Input, OnInit, EventEmitter, Output, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { AccountCompany } from '../../../../models/account-company.model';
import { CompanyService } from '../../../../services/company.service';
import {
  ICountry,
  FullPageMaskService,
  ErrorReportingService,
  Constants,
  SecurityService,
  ProviderType,
  ILookup,
  strictEmailValidator,
} from 'common';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'acc-address-details',
  templateUrl: './address-details.component.html',
  styleUrls: ['./address-details.component.scss'],
})
export class AddressDetailsComponent implements OnInit, OnDestroy {
  @Input() countries: ICountry[];
  @Input() counties: ILookup[];
  @Input() company: AccountCompany;
  @Input() canEdit: boolean;
  @Output() isEditing = new EventEmitter<string>();

  group: UntypedFormGroup;
  showPrsQuestions = false;
  isError = false;
  errorMessages = [];

  private _destroy$ = new Subject<void>();

  constructor(
    private security: SecurityService,
    private companyService: CompanyService,
    private errorService: ErrorReportingService,
    private maskService: FullPageMaskService
  ) {}

  ngOnInit(): void {
    this.createForm(this.company);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  edit(formGroup: UntypedFormGroup) {
    formGroup.enable();
    this.busCountryChanged(this.group.controls.country.value);
    this.regCountryChanged(this.group.controls.regCountry.value);
    this.isError = false;
    if (!this.isPRSAccount()) {
      this.group.controls.regAddressLine1.disable();
      this.group.controls.regAddressLine2.disable();
      this.group.controls.regAddressLine3.disable();
      this.group.controls.regAddressLine4.disable();
      this.group.controls.regPostCode.disable();
      this.group.controls.regCountry.disable();
      this.group.controls.regCounty.disable();
    }
    this.isEditing.emit('address');
  }

  cancel(formGroup: UntypedFormGroup) {
    this.isError = false;
    const newValues = {
      addressLine1: this.company.addressDetails.addressLine1,
      addressLine2: this.company.addressDetails.addressLine2,
      addressLine3: this.company.addressDetails.addressLine3,

      postCode: this.company.addressDetails.postCode,
      country: this.company.addressDetails.country,
      county: this.company.addressDetails.county,

      regAddressLine1: this.company.addressDetails.regAddressLine1,
      regAddressLine2: this.company.addressDetails.regAddressLine2,
      regAddressLine3: this.company.addressDetails.regAddressLine3,
      regAddressLine4: this.company.addressDetails.regAddressLine4,
      regPostCode: this.company.addressDetails.regPostCode,
      regCountry: this.company.addressDetails.regCountry,
      regCounty: this.company.addressDetails.regCounty,

      q1: this.company.prsQ1,
      q2: this.company.prsQ2,
      q3: this.company.prsQ3,

      telephone: this.company.addressDetails.telephone,
      mobile: this.company.addressDetails.mobile,
      fax: this.company.addressDetails.fax,

      email: this.company.addressDetails.email,
    };

    formGroup.reset(newValues);
    formGroup.disable();
    this.isEditing.emit('');
  }

  save(groupValue: any) {
    if (!this.group.valid) {
      return;
    }

    const company: AccountCompany = this.company.getDeepCopy();

    company.addressDetails.addressLine1 = groupValue.addressLine1;
    company.addressDetails.addressLine2 = groupValue.addressLine2;
    company.addressDetails.addressLine3 = groupValue.addressLine3;
    company.addressDetails.addressLine4 = groupValue.addressLine4;
    company.addressDetails.postCode = groupValue.postCode;
    company.addressDetails.country = groupValue.country;
    company.addressDetails.county = groupValue.county ?? '';

    company.addressDetails.regAddressLine1 = groupValue.regAddressLine1 ?? '';
    company.addressDetails.regAddressLine2 = groupValue.regAddressLine2 ?? '';
    company.addressDetails.regAddressLine3 = groupValue.regAddressLine3 ?? '';
    company.addressDetails.regAddressLine4 = groupValue.regAddressLine4 ?? '';
    company.addressDetails.regPostCode = groupValue.regPostCode ?? '';
    company.addressDetails.regCountry = groupValue.regCountry ?? '';
    company.addressDetails.regCounty = groupValue.regCounty ?? '';

    company.prsQ1 = groupValue.q1 ?? '';
    company.prsQ2 = groupValue.q2 ?? '';
    company.prsQ3 = groupValue.q3 ?? '';

    company.addressDetails.telephone = groupValue.telephone;
    company.addressDetails.mobile = groupValue.mobile;

    company.addressDetails.email = groupValue.email;

    this.maskService.showLoading();
    this.isError = false;

    this.companyService
      .updateCompany(company)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (wasRequestOk: boolean) => {
          this.isError = !wasRequestOk;
          if (wasRequestOk) {
            this.showStatus();
            this.company = company;
            this.group.disable();
            this.isEditing.emit('');
            this.maskService.hideLoading();
          }
        },
        error: (errorPayload) => {
          this.errorMessages = this.errorService.getResponseDTOErrors(errorPayload);
          this.isError = true;
          this.maskService.hideLoading();
        },
      });
  }

  isPRSAccount(): boolean {
    const isPRS = this.security.profile.selectedAccount.providerType === ProviderType.PRS;
    return isPRS;
  }

  regCountryChanged(country: string): void {
    if (country.toLowerCase() === Constants.irelandCountryString.toLocaleLowerCase()) {
      this.group.controls.regCounty.enable();
    } else {
      this.group.controls.regCounty.patchValue('');
      this.group.controls.regCounty.disable();
    }

    this.showPrsQuestions = this.isPRSAccount() && this.isEu(country) && country.toLowerCase() !== Constants.irelandCountryString.toLocaleLowerCase();
    this.toggleQuestions(this.showPrsQuestions);
  }

  busCountryChanged(country: string): void {
    if (country.toLowerCase() === Constants.irelandCountryString.toLocaleLowerCase()) {
      this.group.controls.county.enable();
    } else {
      this.group.controls.county.setValue('');
      this.group.controls.county.disable();
    }
  }

  private isEu(country: string): boolean {
    return this.countries.filter((c) => c.name === country)[0].isEU;
  }

  private toggleQuestions(enable: boolean) {
    if (enable) {
      this.group.controls.q1.enable();
      this.group.controls.q3.enable();
      this.group.controls.q2.enable();
    } else {
      this.group.controls.q1.disable();
      this.group.controls.q2.disable();
      this.group.controls.q3.disable();
    }
  }

  showStatus(): void {
    this.errorService.displayInfo('The address was updated successfully');
  }

  createForm(company: AccountCompany) {
    const data = company.addressDetails;
    this.group = new UntypedFormGroup({
      addressLine1: new UntypedFormControl(
        data.addressLine1,
        Validators.required
      ),
      addressLine2: new UntypedFormControl(data.addressLine2),
      addressLine3: new UntypedFormControl(data.addressLine3),
      addressLine4: new UntypedFormControl(data.addressLine4),
      postCode: new UntypedFormControl(data.postCode),
      country: new UntypedFormControl(data.country, Validators.required),
      county: new UntypedFormControl(data.county, Validators.required),

      regAddressLine1: new UntypedFormControl(
        data.regAddressLine1,
        Validators.required
      ),
      regAddressLine2: new UntypedFormControl(data.regAddressLine2),
      regAddressLine3: new UntypedFormControl(data.regAddressLine3),
      regAddressLine4: new UntypedFormControl(data.regAddressLine4),
      regPostCode: new UntypedFormControl(data.regPostCode),
      regCountry: new UntypedFormControl(data.regCountry, Validators.required),
      regCounty: new UntypedFormControl(data.regCounty, Validators.required),

      q1: new UntypedFormControl(company.prsQ1, Validators.required),
      q2: new UntypedFormControl(company.prsQ2, Validators.required),
      q3: new UntypedFormControl(company.prsQ3, Validators.required),

      telephone: new UntypedFormControl(data.telephone, Validators.required),
      mobile: new UntypedFormControl(data.mobile, Validators.required),
      email: new UntypedFormControl(data.email, [Validators.required, strictEmailValidator]),
    });

    this.regCountryChanged(data.regCountry);

    this.group.controls.regCountry.valueChanges.subscribe((country: string) => this.regCountryChanged(country));
    this.group.controls.country.valueChanges.subscribe((country: string) => this.busCountryChanged(country));

    this.group.disable();
  }
}
